import React from 'react';
import { Box, Card, Grid, Typography, Container, Stack, useTheme } from '@mui/material';
import back from '../images/whiteDark.jpg';
import Contact from '../components/Contact'; // Assuming this is your form component
import ContactMessage from '../components/ContactMessage'; // Assuming this is your form component
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';

// Reusable component for the contact info cards
const ContactCard = ({ icon, title, text, link, linkText }) => {
    const theme = useTheme();

    return (
        <Card
            sx={{
                width: '100%',
                height: '100%',
                borderRadius: '20px',
                background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f9f9f9',
                boxShadow: theme.shadows[4],
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                    transform: 'translateY(-10px)',
                    boxShadow: theme.shadows[10],
                },
            }}
        >
            <Box sx={{ color: '#a974d0', mb: 2 }}>
                {icon}
            </Box>
            <Typography
                variant="h4"
                sx={{
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 'bold',
                    color: theme.palette.text.primary,
                    mb: 1,
                }}
            >
                {title}
            </Typography>
            <Typography
                variant="body1"
                sx={{
                    fontFamily: "Montserrat, sans-serif",
                    color: theme.palette.text.secondary,
                    mb: 2,
                }}
            >
                {text}
            </Typography>
            {link && (
                <Typography
                    variant="h5"
                    component="a"
                    href={link}
                    sx={{
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: 'bold',
                        color: theme.palette.primary.main,
                        textDecoration: 'none',
                        '&:hover': {
                            textDecoration: 'underline',
                        },
                    }}
                >
                    {linkText}
                </Typography>
            )}
        </Card>
    );
};

function ContactUs() {
    const theme = useTheme();

    return (
        <>
            {/* Hero Section */}
            <Box
                sx={{
                    height: { md: '60vh', xs: '40vh' },
                    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${back})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: 'white',
                    textAlign: 'center',
                    p: 2,
                }}
                className='no-select'
            >
                <Typography
                    variant="h2"
                    component="h1"
                    sx={{
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: '600',
                        fontSize: { xs: '3rem', md: '6rem' },
                        mb: 2,
                    }}
                >
                    Get in touch
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: '500',
                        fontSize: { xs: '0.875rem', md: '1.2rem' },
                    }}
                >
                    <a href='/' style={{ textDecoration: 'none', color: 'white' }}>Home</a> / <span style={{ color: theme.palette.secondary.main }}>Get In Touch</span>
                </Typography>
            </Box>

            {/* Introduction and Main Content */}
            <Container maxWidth="lg" sx={{ my: { xs: 8, md: 12 } }}>
                <Typography
                    variant="h6"
                    component="p"
                    align="center"
                    sx={{
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: '500',
                        fontSize: { xs: '14px', md: '20px' },
                        color: theme.palette.text.secondary,
                        mb: 8,
                        maxWidth: '800px',
                        mx: 'auto'
                    }}
                >
                    Get in touch with our experts for accurate project estimations, intelligent business decisions, and best-in-class digital outcomes.
                </Typography>

                <Grid container spacing={{ xs: 4, md: 6 }}>
                    {/* Left side: Contact Form */}
                    <Grid item xs={12} md={6}>
                        <Card
                            sx={{
                                width: '100%',
                                p: { xs: 3, md: 6 },
                                borderRadius: '20px',
                                boxShadow: theme.shadows[10],
                                background: 'black',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                            }}
                        >
                            <Typography
                                variant="h4"
                                align="center"
                                sx={{
                                    color: '#D3D3D4',
                                    fontFamily: "Montserrat, sans-serif",
                                    fontWeight: 'bold',
                                    mb: 4,
                                }}
                            >
                                Leave Us a Message
                            </Typography>
                            {/* Assuming ContactMessage is the form component */}
                            <ContactMessage />
                        </Card>
                    </Grid>

                    {/* Right side: Contact Info Cards */}
                    <Grid item xs={12} md={6}>
                        <Stack spacing={4} sx={{ height: '100%' }}>
                            <ContactCard
                                icon={<PhoneIcon sx={{ fontSize: '4rem' }} />}
                                title="Hotline"
                                text="Success Begins with a Conversation – Let’s Talk!"
                                link="tel:+94770832340"
                                linkText="+94 770 832 340"
                            />
                            <ContactCard
                                icon={<EmailIcon sx={{ fontSize: '4rem' }} />}
                                title="Email Us"
                                text="Transform Your Ideas into Reality – Contact Us Now!"
                                link="mailto:info@ccsoftwares.com"
                                linkText="info@ccsoftwares.com"
                            />
                        </Stack>
                    </Grid>
                </Grid>
            </Container>
        </>
    );
}

export default ContactUs;